import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { SearchIcon } from 'lucide-react';
type Category = 'all' | 'injectables' | 'laser' | 'body' | 'medical' | 'general';
interface Keyword {
  keyword: string;
  category: Category;
  searches: number;
  cpcLow: number;
  cpcHigh: number;
}
const keywords: Keyword[] = [
// Injectables
{
  keyword: 'Botox near me',
  category: 'injectables',
  searches: 8100,
  cpcLow: 8,
  cpcHigh: 15
}, {
  keyword: 'Botox San Ramon',
  category: 'injectables',
  searches: 110,
  cpcLow: 5,
  cpcHigh: 10
}, {
  keyword: 'lip fillers near me',
  category: 'injectables',
  searches: 4400,
  cpcLow: 7,
  cpcHigh: 14
}, {
  keyword: 'Sculptra near me',
  category: 'injectables',
  searches: 1300,
  cpcLow: 5,
  cpcHigh: 10
}, {
  keyword: 'PDO thread lift near me',
  category: 'injectables',
  searches: 1000,
  cpcLow: 6,
  cpcHigh: 12
}, {
  keyword: 'Kybella near me',
  category: 'injectables',
  searches: 880,
  cpcLow: 5,
  cpcHigh: 10
},
// Laser & Skin
{
  keyword: 'laser hair removal near me',
  category: 'laser',
  searches: 18100,
  cpcLow: 5,
  cpcHigh: 12
}, {
  keyword: 'laser hair removal San Ramon',
  category: 'laser',
  searches: 170,
  cpcLow: 4,
  cpcHigh: 8
}, {
  keyword: 'Halo laser near me',
  category: 'laser',
  searches: 1600,
  cpcLow: 5,
  cpcHigh: 10
}, {
  keyword: 'BBL laser treatment near me',
  category: 'laser',
  searches: 720,
  cpcLow: 5,
  cpcHigh: 10
}, {
  keyword: 'microneedling near me',
  category: 'laser',
  searches: 6600,
  cpcLow: 3,
  cpcHigh: 7
}, {
  keyword: 'RF microneedling near me',
  category: 'laser',
  searches: 1900,
  cpcLow: 4,
  cpcHigh: 9
}, {
  keyword: 'hydrafacial near me',
  category: 'laser',
  searches: 6600,
  cpcLow: 3,
  cpcHigh: 7
}, {
  keyword: 'chemical peel near me',
  category: 'laser',
  searches: 3600,
  cpcLow: 3,
  cpcHigh: 6
},
// Body & Weight Loss
{
  keyword: 'CoolSculpting near me',
  category: 'body',
  searches: 6600,
  cpcLow: 8,
  cpcHigh: 18
}, {
  keyword: 'CoolSculpting San Ramon',
  category: 'body',
  searches: 40,
  cpcLow: 5,
  cpcHigh: 12
}, {
  keyword: 'medical weight loss near me',
  category: 'body',
  searches: 4400,
  cpcLow: 8,
  cpcHigh: 18
}, {
  keyword: 'semaglutide near me',
  category: 'body',
  searches: 5400,
  cpcLow: 7,
  cpcHigh: 15
}, {
  keyword: 'Cooltone near me',
  category: 'body',
  searches: 880,
  cpcLow: 5,
  cpcHigh: 10
},
// Medical & Wellness
{
  keyword: 'IV therapy near me',
  category: 'medical',
  searches: 6600,
  cpcLow: 3,
  cpcHigh: 7
}, {
  keyword: 'TRT near me',
  category: 'medical',
  searches: 3600,
  cpcLow: 6,
  cpcHigh: 14
}, {
  keyword: 'bioidentical hormone therapy near me',
  category: 'medical',
  searches: 1300,
  cpcLow: 5,
  cpcHigh: 12
}, {
  keyword: 'PRP hair restoration near me',
  category: 'medical',
  searches: 880,
  cpcLow: 5,
  cpcHigh: 10
},
// General
{
  keyword: 'med spa near me',
  category: 'general',
  searches: 18100,
  cpcLow: 6,
  cpcHigh: 14
}, {
  keyword: 'med spa San Ramon',
  category: 'general',
  searches: 210,
  cpcLow: 5,
  cpcHigh: 10
}, {
  keyword: 'best med spa San Ramon',
  category: 'general',
  searches: 50,
  cpcLow: 4,
  cpcHigh: 9
}];
const categories = [{
  id: 'all' as Category,
  label: 'All Keywords',
  color: '#1B2A4A'
}, {
  id: 'injectables' as Category,
  label: 'Injectables',
  color: '#1B2A4A'
}, {
  id: 'laser' as Category,
  label: 'Laser & Skin',
  color: '#C9A96E'
}, {
  id: 'body' as Category,
  label: 'Body & Weight',
  color: '#2A3F6A'
}, {
  id: 'medical' as Category,
  label: 'Medical',
  color: '#8B7355'
}, {
  id: 'general' as Category,
  label: 'General',
  color: '#A8B4C4'
}];
export function KeywordTable() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const filteredKeywords = activeCategory === 'all' ? keywords : keywords.filter(k => k.category === activeCategory);
  const topKeywords = [...keywords].sort((a, b) => b.searches - a.searches).slice(0, 8).map(k => ({
    name: k.keyword.length > 25 ? k.keyword.slice(0, 22) + '...' : k.keyword,
    searches: k.searches,
    category: k.category
  }));
  const maxSearches = Math.max(...keywords.map(k => k.searches));
  const getCategoryColor = (cat: Category) => {
    return categories.find(c => c.id === cat)?.color || '#1B2A4A';
  };
  return <section id="keywords" className="py-24 md:py-32 bg-white" ref={ref} data-id="element-88">
      <div className="max-w-6xl mx-auto px-6" data-id="element-89">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-90">
          <p className="text-[#C9A96E] text-sm font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-91">
            Keywords
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-[#1B2A4A]" data-id="element-92">
            Target Keywords & Search Demand
          </h2>
          <p className="text-[#666] mt-4 max-w-2xl mx-auto" data-id="element-93">
            These are the top keywords we'll target, along with estimated
            monthly search volume and cost per click in your market.
          </p>
        </motion.div>

        {/* Top Keywords Chart */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-[#F8F9FA] rounded-2xl p-6 md:p-8 mb-12" data-id="element-94">
          <h3 className="text-lg font-semibold text-[#1B2A4A] mb-6" data-id="element-95">
            Top Keywords by Search Volume
          </h3>
          <div className="h-80" data-id="element-96">
            <ResponsiveContainer width="100%" height="100%" data-id="element-97">
              <BarChart data={topKeywords} layout="vertical" margin={{
              top: 0,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-98">
                <XAxis type="number" hide data-id="element-99" />
                <YAxis type="category" dataKey="name" width={180} tick={{
                fill: '#666',
                fontSize: 12
              }} axisLine={false} tickLine={false} data-id="element-100" />
                <Tooltip formatter={(value: number) => [value.toLocaleString() + ' searches/mo', 'Volume']} contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }} data-id="element-101" />
                <Bar dataKey="searches" radius={[0, 4, 4, 0]} animationDuration={1500} data-id="element-102">
                  {topKeywords.map((entry, index) => <Cell key={`cell-${index}`} fill={getCategoryColor(entry.category as Category)} data-id="element-103" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Category Tabs */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="flex flex-wrap gap-2 mb-8" data-id="element-104">
          {categories.map(cat => <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCategory === cat.id ? 'bg-[#1B2A4A] text-white' : 'bg-[#F8F9FA] text-[#666] hover:bg-[#1B2A4A]/10'}`} data-id="element-105">
              {cat.label}
            </button>)}
        </motion.div>

        {/* Keywords Table */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="bg-white rounded-2xl shadow-sm border border-[#1B2A4A]/5 overflow-hidden" data-id="element-106">
          <div className="overflow-x-auto" data-id="element-107">
            <table className="w-full" data-id="element-108">
              <thead data-id="element-109">
                <tr className="bg-[#1B2A4A] text-white" data-id="element-110">
                  <th className="text-left p-4 font-medium text-sm" data-id="element-111">Keyword</th>
                  <th className="text-right p-4 font-medium text-sm" data-id="element-112">
                    Monthly Searches
                  </th>
                  <th className="text-right p-4 font-medium text-sm" data-id="element-113">
                    Est. CPC
                  </th>
                  <th className="p-4 font-medium text-sm w-48" data-id="element-114">Volume</th>
                </tr>
              </thead>
              <tbody data-id="element-115">
                {filteredKeywords.map((kw, index) => <motion.tr key={kw.keyword} initial={{
                opacity: 0,
                x: -20
              }} animate={isInView ? {
                opacity: 1,
                x: 0
              } : {}} transition={{
                duration: 0.3,
                delay: 0.5 + index * 0.03
              }} className="border-b border-[#1B2A4A]/5 hover:bg-[#F8F9FA]/50" data-id="element-116">
                    <td className="p-4" data-id="element-117">
                      <div className="flex items-center gap-3" data-id="element-118">
                        <SearchIcon className="w-4 h-4 text-[#C9A96E]" data-id="element-119" />
                        <span className="text-sm text-[#1B2A4A]" data-id="element-120">
                          {kw.keyword}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-right" data-id="element-121">
                      <span className="text-sm font-semibold text-[#1B2A4A]" data-id="element-122">
                        {kw.searches.toLocaleString()}
                      </span>
                    </td>
                    <td className="p-4 text-right" data-id="element-123">
                      <span className="text-sm text-[#666]" data-id="element-124">
                        ${kw.cpcLow}–${kw.cpcHigh}
                      </span>
                    </td>
                    <td className="p-4" data-id="element-125">
                      <div className="w-full bg-[#F8F9FA] rounded-full h-2 overflow-hidden" data-id="element-126">
                        <motion.div initial={{
                      width: 0
                    }} animate={isInView ? {
                      width: `${kw.searches / maxSearches * 100}%`
                    } : {}} transition={{
                      duration: 0.8,
                      delay: 0.6 + index * 0.03
                    }} className="h-full rounded-full" style={{
                      backgroundColor: getCategoryColor(kw.category)
                    }} data-id="element-127" />
                      </div>
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="mt-6 text-sm text-[#666] text-center" data-id="element-128">
          This is a curated selection of the highest-impact keywords. The full
          campaign will include
          <span className="text-[#1B2A4A] font-medium" data-id="element-129"> 50+ keywords</span>{' '}
          across all your services, including local variations for San Ramon,
          Danville, Dublin, Pleasanton, and Walnut Creek.
        </motion.p>
      </div>
    </section>;
}